import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined"
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined"
import Badge from "@mui/material/Badge"
import Button from "@mui/material/Button"
import Divider from "@mui/material/Divider"
import IconButton from "@mui/material/IconButton"
import Popover from "@mui/material/Popover"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import {
  selectCRMSettings,
  selectCRMSettingsLoading,
  selectEmailAuthorizationLoading,
  selectEmailProviderAuthorized,
  selectSelectedMLSID,
} from "../reducer"
import { SETTINGS_TABS, createSettingsTabURL } from "../settings"
import { mlsPurchasePath } from "../settings/campaignNavigation"
import PercentageCircularProgress from "./PercentageCircularProgress"

export default function SetupProgressChecklistButton() {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [progressFromAPI, setProgressFromAPI] = useState({
    imported_candidates: false,
    updated_crm_stages: false,
  })
  const [isLoadingProgressFromAPI, setIsLoadingProgressFromAPI] =
    useState(false)
  const iconButtonRef = useRef()
  const navigate = useNavigate()
  const emailProviderAuthorized = useSelector(selectEmailProviderAuthorized)
  const emailProviderAuthorizationLoading = useSelector(
    selectEmailAuthorizationLoading
  )
  const crmSettings = useSelector(selectCRMSettings).settings
  const crmSettingsLoading = useSelector(selectCRMSettingsLoading)
  const selectedMLS = useSelector(selectSelectedMLSID)
  const axios = useAuthorizedAxiosClient()
  const crmSettingsUpdated = !!(
    crmSettings.broker_name || crmSettings.broker_title
  )
  const mlsSelected = !!selectedMLS
  const setupBoolSum =
    emailProviderAuthorized +
    crmSettingsUpdated +
    progressFromAPI.imported_candidates +
    progressFromAPI.updated_crm_stages +
    mlsSelected
  const SETUP_FACTOR_COUNT = 5
  const setupProgress = (setupBoolSum / SETUP_FACTOR_COUNT) * 100
  const missingFactors = SETUP_FACTOR_COUNT - setupBoolSum
  const isLoading =
    isLoadingProgressFromAPI ||
    emailProviderAuthorizationLoading ||
    crmSettingsLoading

  function handleConnectEmailButtonClicked() {
    closePopover()
    navigate(createSettingsTabURL(SETTINGS_TABS.configureEmail))
  }

  function handleUpdateCRMSettingsButtonClicked() {
    closePopover()
    navigate(createSettingsTabURL(SETTINGS_TABS.crmSettings))
  }

  function handleImportFromCandidatesButtonClicked() {
    closePopover()
    navigate("/candidates")
  }

  function handleUpdateCandidateStagesButtonClicked() {
    closePopover()
    navigate("/pipeline")
  }

  function handlePurchaseMlsButtonClicked() {
    closePopover()
    navigate(mlsPurchasePath())
  }

  function closePopover() {
    setPopoverOpen(!popoverOpen)
  }

  useEffect(
    () => {
      setIsLoadingProgressFromAPI(true)
      axios
        .get("/crm/api/crm-setup-progress/")
        .then(({ data }) => setProgressFromAPI(data))
        .finally(() => setIsLoadingProgressFromAPI(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <>
      <IconButton
        ref={iconButtonRef}
        sx={{ display: missingFactors ? "inline-flex" : "none" }}
        onClick={() => setPopoverOpen(true)}
      >
        <StyledBadge badgeContent={missingFactors} color="secondary">
          <ChecklistOutlinedIcon />
        </StyledBadge>
      </IconButton>
      <Popover
        open={popoverOpen}
        onClose={closePopover}
        anchorEl={iconButtonRef.current}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
      >
        <PopoverContentColumn>
          <Row sx={{ alignItems: "center", gap: 2, p: 2 }}>
            {isLoading ? (
              <>
                <Skeleton variant="circular" width={48} height={48} />
                <Skeleton variant="text" width={160} height={32} />
              </>
            ) : (
              <>
                <PercentageCircularProgress
                  progress={setupProgress}
                  size={48}
                  fontSize="0.83em"
                />
                <Typography variant="h4" sx={{ fontSize: "1.05em" }}>
                  Complete your CRM Setup
                </Typography>
              </>
            )}
          </Row>
          <Divider />
          <Column sx={{ gap: 2, p: 2, width: "100%" }}>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <Row key={i} sx={{ alignItems: "center", gap: 2 }}>
                  <Row sx={{ width: "64px", justifyContent: "center" }}>
                    <Skeleton variant="circular" width={24} height={24} />
                  </Row>
                  <RightsideRow>
                    <Skeleton variant="text" width={170} height={26} />
                  </RightsideRow>
                </Row>
              ))
            ) : (
              <>
                <ProgressStep
                  title="Connect your email"
                  checked={emailProviderAuthorized}
                  onClick={handleConnectEmailButtonClicked}
                />
                <ProgressStep
                  title="Update CRM settings"
                  checked={crmSettingsUpdated}
                  onClick={handleUpdateCRMSettingsButtonClicked}
                />
                <ProgressStep
                  title="Import your candidates"
                  checked={progressFromAPI.imported_candidates}
                  onClick={handleImportFromCandidatesButtonClicked}
                />
                <ProgressStep
                  title="Update candidate stages"
                  checked={progressFromAPI.updated_crm_stages}
                  onClick={handleUpdateCandidateStagesButtonClicked}
                />
                <ProgressStep
                  title="Purchase MLS"
                  checked={mlsSelected}
                  onClick={handlePurchaseMlsButtonClicked}
                />
              </>
            )}
          </Column>
        </PopoverContentColumn>
      </Popover>
    </>
  )
}

const PopoverContentColumn = styled(Column)(({ theme }) => ({
  width: "370px",
  height: "310px",
}))

const StyledCheckCircleIcon = styled(CheckCircleOutlinedIcon, {
  shouldForwardProp: (prop) => prop !== "checked",
})(({ theme, checked }) => ({
  fill: checked ? theme.palette.primary.main : theme.palette.colors.gray400,
  fontSize: "24px",
}))

const SmallButton = styled(Button)(({ theme }) => ({
  padding: 0,
  paddingInline: theme.spacing(1),
  minWidth: "64px",
}))

const RightsideRow = styled(Row)(({ theme }) => ({
  gap: theme.spacing(1),
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
}))

const StyledBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    height: "14px",
    width: "14px",
    minWidth: "14px",
    fontSize: "0.6rem",
    right: "-4px",
  },
}))

function ProgressStep({ checked, title, onClick }) {
  return (
    <Row sx={{ alignItems: "center", gap: 2 }}>
      <Row sx={{ width: "64px", justifyContent: "center" }}>
        <StyledCheckCircleIcon checked={checked} />
      </Row>
      <RightsideRow>
        <Typography variant="h5">{title}</Typography>
        {checked ? null : (
          <SmallButton
            size="small"
            color="primary"
            variant="outlined"
            onClick={onClick}
          >
            Fix
          </SmallButton>
        )}
      </RightsideRow>
    </Row>
  )
}
